import React, { useMemo, useState } from 'react';
import {
  BucketDefType,
  BucketKind,
  INamedBucketDefFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { builderBinningSelector } from '../../../../redux/selectors/binning';
import {
  setBreakdownGroupBinning,
  setSelectedBinning,
} from '../../../../redux/slices/oldBuilder';
import {
  IBasicBinning,
  IDateBinning,
  IEnumBinning,
  INamedBinning,
  IRangeBinning,
} from '../../../../redux/slices/oldBuilder/binning/types';
import BinningSelector from './BinningSelector';
import BucketDef from './BucketDef';
import {
  bucketWithCustomName,
  narrowBucketsByKind,
} from '../../utils/builderUtils';
import { selectOpenedModalType } from '../../../../redux/selectors/modal';
import { ModalSliceType } from '../../../../redux/slices/modal';
import SaveNamedBucketDefModal from './SaveNamedBucketDefModal';
import { activeSchemaIdSelector } from '../../../../redux/selectors/builder';
import { isNil } from 'lodash';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  supportedBucketKinds: BucketKind[];
  selectedFieldKey: string | null;
  defaultNamedBucketDef: INamedBucketDefFieldsFragment | null;
  groupIndex?: number;
}

/**
 * BuilderBinning is a controller for both Breakdown Binning
 * and Histogram Binning features.
 *
 * It renders BinningSelector menu where user can select Basic or Named binning.
 * Once selected, BucketDef will render binning fields
 */
export default ({
  supportedBucketKinds,
  selectedFieldKey,
  groupIndex,
  defaultNamedBucketDef,
}: IProps) => {
  const [cachedNamedBucketDef, setCachedNamedBucketDef] =
    useState<INamedBucketDefFieldsFragment | null>(defaultNamedBucketDef);

  const dispatch = useAppDispatch();

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const selectedBinning = useAppSelector((state) =>
    builderBinningSelector(state, groupIndex),
  );

  const isSaveBucketDefModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.SaveBucketDef, ''),
  );

  const basicBinning: IBasicBinning | null = useMemo(() => {
    if (!selectedBinning) {
      return null;
    }

    // In case of named binning, values from namedBucketDef would be populated
    if (selectedBinning.type === BucketDefType.Named) {
      const namedBucketDef = selectedBinning.namedBucketDef;
      const { kind, buckets, enumBucketSplitOthers } = namedBucketDef;

      if (kind === BucketKind.Range) {
        return {
          type: BucketDefType.Basic,
          namedBucketDef,
          kind,
          customBuckets: buckets
            .filter(narrowBucketsByKind(kind))
            .map(bucketWithCustomName),
          isAutoRangeBucket: false,
          updateKey: 0,
        } satisfies IRangeBinning;
      } else if (kind === BucketKind.Enum) {
        return {
          type: BucketDefType.Basic,
          namedBucketDef,
          kind,
          customBuckets: buckets
            .filter(narrowBucketsByKind(kind))
            .map(bucketWithCustomName),
          enumBucketSplitOthers: enumBucketSplitOthers ?? true,
          updateKey: 0,
        } satisfies IEnumBinning;
      } else if (kind === BucketKind.Date) {
        return {
          type: BucketDefType.Basic,
          namedBucketDef,
          kind,
          customBuckets: buckets.filter(narrowBucketsByKind(kind)),
          updateKey: 0,
        } satisfies IDateBinning;
      } else {
        throw new UnreachableCaseError(kind);
      }
    } else if (selectedBinning.type === BucketDefType.Basic) {
      return selectedBinning;
    } else {
      throw new UnreachableCaseError(selectedBinning);
    }
  }, [selectedBinning]);

  const handleUpdateSavedNamedBucketDef = (
    savedBucketDef: INamedBucketDefFieldsFragment,
  ) => {
    setCachedNamedBucketDef(savedBucketDef);

    const newBinning = {
      type: BucketDefType.Named,
      kind: savedBucketDef.kind,
      namedBucketDef: savedBucketDef,
      updateKey: selectedBinning?.updateKey ? selectedBinning.updateKey + 1 : 0,
    } satisfies INamedBinning;

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
  };

  return (
    <>
      <BinningSelector
        activeSchemaId={activeSchemaId}
        supportedBucketKinds={supportedBucketKinds}
        selectedFieldKey={selectedFieldKey}
        groupIndex={groupIndex}
        onSelectNamedBucket={setCachedNamedBucketDef}
      />
      {basicBinning && (
        <BucketDef basicBinning={basicBinning} groupIndex={groupIndex} />
      )}
      {isSaveBucketDefModalOpen && selectedBinning && (
        <SaveNamedBucketDefModal
          activeSchemaId={activeSchemaId}
          selectedFieldKey={selectedFieldKey}
          selectedBinning={selectedBinning}
          cachedNamedBucketDef={cachedNamedBucketDef}
          onSubmit={handleUpdateSavedNamedBucketDef}
        />
      )}
    </>
  );
};
